import {
  type Consultation,
  useRxCollectionConsultation,
} from "$global-providers/rxdb-provider/collections/consultation";
import {
  type Disease,
  useRxCollectionDisease,
} from "$global-providers/rxdb-provider/collections/disease";
import { useRxCollectionInvestigationActivity } from "$global-providers/rxdb-provider/collections/investigation-activity";
import { useRxCollectionPrescription } from "$global-providers/rxdb-provider/collections/prescription";
import {
  type Symptom,
  useRxCollectionSymptom,
} from "$global-providers/rxdb-provider/collections/symptom";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { subMilliseconds } from "date-fns";
import { useEffect, useState } from "react";
import type { RxDatabase } from "rxdb";
import {
  mapDiseaseCodeToUri,
  mapDiseaseToPatientClinicalResource,
} from "../../consultation/_lib/fhir-mapper";
import { getDiseaseCode } from "../../vaccinations/_lib/get-disease-code";
import { GENERAL_RECENCY_WINDOW_MS } from "./constants";
import type { RxDBCollections } from "./db";
import { FHIRResourceTypes } from "./db/collections/common";
import {
  ClinicalResourceSubmissionStatus,
  type PatientClinicalResource,
  RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME,
} from "./db/collections/patient-clinical-resource";
import {
  constructResourceId,
  mapInvestigationToFhirResources,
  shouldAddDiseaseToCDS,
} from "./mappers";

export const useHydrateDatabase = ({
  patientId,
  db,
}: {
  patientId: string;
  db: RxDatabase<RxDBCollections> | null;
}) => {
  const [isHydrating, setIsHydrating] = useState(false);
  const {
    replicationState: {
      consultation: { isPulling: isConsultationPulling },
      symptom: { isPulling: isSymptomPulling },
      disease: { isPulling: isDiseasePulling },
      investigationActivity: { isPulling: isInvestigationActivityPulling },
      prescription: { isPulling: isPrescriptionPulling },
    },
  } = useRxDBContext();
  const isPullingElephantDb =
    isConsultationPulling ||
    isSymptomPulling ||
    isDiseasePulling ||
    isInvestigationActivityPulling ||
    isPrescriptionPulling;
  const consultationCollection = useRxCollectionConsultation();
  const symptomCollection = useRxCollectionSymptom();
  const diseaseCollection = useRxCollectionDisease();
  const investigationActivityCollection =
    useRxCollectionInvestigationActivity();
  const prescriptionCollection = useRxCollectionPrescription();

  useEffect(() => {
    if (
      !patientId ||
      !db ||
      !consultationCollection ||
      !symptomCollection ||
      !diseaseCollection ||
      !investigationActivityCollection ||
      !prescriptionCollection ||
      isPullingElephantDb
    ) {
      return;
    }

    const hydrateDatabase = async () => {
      setIsHydrating(true);
      const consultations = await consultationCollection
        ?.find({
          selector: {
            patientId,
            "meta.updatedAt": {
              $gt: new Date(
                Date.now() - GENERAL_RECENCY_WINDOW_MS,
              ).toISOString(),
            },
          },
        })
        ?.exec();

      // Extract unique symptomIds & diseaseIds
      const symptomIds = new Set<string>();
      const diseaseIds = new Set<string>();

      for (const consultation of consultations ?? []) {
        for (const id of consultation.symptomIds ?? []) symptomIds.add(id);
        for (const id of consultation.diseaseIds ?? []) diseaseIds.add(id);
      }
      const diseases = await diseaseCollection
        ?.find({
          selector: {
            id: { $in: Array.from(diseaseIds) },
          },
        })
        ?.exec();

      const diseaseMap: Record<string, Disease> = Object.fromEntries(
        diseases?.map((d) => [d.id, d]) ?? [],
      );
      const symptoms = await symptomCollection
        ?.find({
          selector: {
            id: {
              $in: Array.from(symptomIds),
            },
          },
        })
        ?.exec();

      // Stitch together consultation meta along with the additional symptom info
      const consultationSymptomRows = consultations?.flatMap((consultation) => {
        return consultation.symptomIds
          ? consultation.symptomIds.map((symptomId) => {
              const symptom = symptoms?.find(
                (symptom) => symptom.id === symptomId,
              );
              return { meta: consultation.meta, symptom };
            })
          : [];
      });

      // Deduplicate the list holding onto newer items
      const deduplicatedConsultationSymptomRows = consultationSymptomRows.length
        ? consultationSymptomRows
            .sort(
              (a, b) =>
                new Date(b.meta.updatedAt).getTime() -
                new Date(a.meta.updatedAt).getTime(),
            )
            .reduce(
              (
                acc: { symptom: Symptom; meta: Consultation["meta"] }[],
                current,
              ) => {
                if (current.symptom?.id && current.symptom.externalCoding) {
                  const existing = acc.find(
                    (item) => item.symptom.id === current.symptom?.id,
                  );
                  if (!existing) {
                    acc.push(
                      current as {
                        symptom: Symptom;
                        meta: Consultation["meta"];
                      },
                    );
                  }
                }
                return acc;
              },
              [],
            )
        : [];
      const symptomResources = deduplicatedConsultationSymptomRows.reduce(
        (acc: PatientClinicalResource[], { meta, symptom }) => {
          const uri = symptom.externalCoding.snomed
            ? `${symptom.externalCoding.snomed.system}/${symptom.externalCoding.snomed.code}`
            : null;

          if (uri) {
            acc.push({
              id: constructResourceId(
                uri,
                patientId,
                ClinicalResourceSubmissionStatus.Submitted,
              ),
              fhirResource: {
                resourceType: FHIRResourceTypes.symptom,
                diagnosticReport: null,
                externalCoding: {
                  icd10: null,
                  icd11: null,
                  loinc: null,
                  snomed: { uri },
                  elephantInternal: null,
                },
              },
              patient: { id: patientId },
              submissionStatus: ClinicalResourceSubmissionStatus.Submitted,
              meta,
            });
          }

          return acc;
        },
        [],
      );
      const diseaseResources = Object.values(diseaseMap)
        .filter((disease) => shouldAddDiseaseToCDS(getDiseaseCode(disease)))
        .map((disease) => ({
          ...mapDiseaseToPatientClinicalResource(
            getDiseaseCode(disease),
            disease.meta.createdByUserId,
          ),
          patient: { id: patientId },
          submissionStatus: ClinicalResourceSubmissionStatus.Submitted,
          id: constructResourceId(
            mapDiseaseCodeToUri(getDiseaseCode(disease)),
            patientId,
            ClinicalResourceSubmissionStatus.Submitted,
          ),
        }));

      const investigationActivities = await investigationActivityCollection
        ?.find({
          selector: {
            patientId,
          },
        })
        ?.exec();

      // TODO: ENG-4792 understand why the date filtering isn't working in rx query for investigations

      const investigationResources = Array.from(
        investigationActivities
          ?.filter(
            (activity) =>
              new Date(activity.meta.updatedAt) >
              new Date(Date.now() - GENERAL_RECENCY_WINDOW_MS),
          )
          .flatMap((investigation) =>
            mapInvestigationToFhirResources({ investigation, patientId }),
          )
          .reduce((acc, resource) => {
            const id = resource.id;
            if (!acc.has(id)) {
              acc.set(id, resource);
            }
            return acc;
          }, new Map<string, PatientClinicalResource>())
          .values() ?? [],
      );

      const prescriptions = await prescriptionCollection
        ?.find({
          selector: {
            patientId,
          },
        })
        ?.exec();

      // TODO: ENG-4792 understand why the date filtering isn't working in rx query for prescriptions
      const filteredPrescriptions = prescriptions?.filter(
        (prescription) =>
          new Date(prescription.meta.updatedAt) >
          new Date(Date.now() - GENERAL_RECENCY_WINDOW_MS),
      );
      const prescriptionResources =
        filteredPrescriptions
          ?.filter((prescription) => prescription.clinicalProduct?.id)
          .map((prescription) => {
            const uri = `http://elephant.healthcare/${prescription.clinicalProduct!.id}`;
            return {
              id: constructResourceId(
                uri,
                patientId,
                ClinicalResourceSubmissionStatus.Submitted,
              ),
              fhirResource: {
                resourceType: FHIRResourceTypes.prescription,
                diagnosticReport: null,
                externalCoding: {
                  icd10: null,
                  icd11: null,
                  loinc: null,
                  snomed: null,
                  elephantInternal: { uri },
                },
              },
              patient: { id: patientId },
              submissionStatus: ClinicalResourceSubmissionStatus.Submitted,
              meta: prescription.meta,
            };
          }) ?? [];

      await db?.[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]?.bulkUpsert([
        ...symptomResources,
        ...diseaseResources,
        ...investigationResources,
        ...prescriptionResources,
      ]);

      setIsHydrating(false);
    };

    const expireResourcesOutsideRecencyWindow = async () => {
      const expiryDateTime = subMilliseconds(
        new Date(),
        GENERAL_RECENCY_WINDOW_MS,
      );

      await db?.[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]
        ?.find({
          selector: {
            submissionStatus: ClinicalResourceSubmissionStatus.Submitted,
            "meta.createdAt": { $lte: expiryDateTime.toISOString() },
          },
        })
        ?.remove();
    };

    void hydrateDatabase();
    void expireResourcesOutsideRecencyWindow();
  }, [
    isPullingElephantDb,
    db,
    db?.[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]?.bulkUpsert,
    db?.[RXDB_PATIENT_CLINICAL_RESOURCE_COLLECTION_NAME]?.find,
    patientId,
    consultationCollection,
    symptomCollection,
    diseaseCollection,
    investigationActivityCollection,
    prescriptionCollection,
  ]);
  return { isHydrating };
};
